import { useEffect, useState } from 'react';
import { Box, Link, Modal, Text, Icon } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { InteractiveList } from '@nimbus-ds/patterns';
import { useNavegate } from 'App/hooks';
import { Stack } from 'commons/components';
import { toCamelCase } from 'commons/utils';
import { logEvent } from 'commons/utils/tracking';
import { openWindow } from 'commons/utils/window';
import {
  OTP_MODAL_RECOMMENDATIONS_CLICK,
  OTP_MODAL_RECOMMENDATIONS_SHOW,
} from 'config/trackingEvents';
import { ACCOUNT_ROUTES } from 'domains/Account/accountRoutes';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useAdminLinks } from 'domains/Auth/hooks';
import useGetUserInfo from 'domains/Auth/hooks/useGetUserInfo/useGetUserInfo';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';

const REAL_VULNERABILITIES = [
  'is_password_leaked',
  'is_password_vulnerable',
] as const;

type RealVulnerability = typeof REAL_VULNERABILITIES[number];
type VulnerabilityState = RealVulnerability | 'no_vulnerabilities';

function VulnerablePasswordAlert() {
  const vulnerablePasswordTranslations = useTranslationDashboard(
    'vulnerablePasswordAlert',
  );
  const securityModalTranslations = useTranslationDashboard('securityModal');
  const {
    removeSearchParams,
    hasSearchParam,
    getQueryParam,
    replace,
    pathname,
  } = useNavegate();
  const { getNewAdminLink } = useAdminLinks();
  const [vulnerability, setVulnerability] = useState<VulnerabilityState>();
  const [isFromOtp, setIsFromOtp] = useState(false);
  const { validateRoles } = useRoles();
  const { id: currentUserId } = useGetUserInfo();
  const guideUrl = vulnerablePasswordTranslations(
    validateRoles('full') ? 'linkFull' : 'linkNotFull',
  );

  useEffect(() => {
    if (isFromOtp && vulnerability !== undefined) {
      logEvent(OTP_MODAL_RECOMMENDATIONS_SHOW, {});
    }
  }, [isFromOtp, vulnerability]);

  useEffect(() => {
    if (vulnerability !== undefined) return;

    const loginType = getQueryParam('login_type');

    const presentVulnerabilities = REAL_VULNERABILITIES.filter((param) =>
      hasSearchParam(param),
    );

    const priorityList: RealVulnerability[] = [
      'is_password_leaked',
      'is_password_vulnerable',
    ];
    const prioritizedVulnerability = priorityList.find(
      (vulnerability: RealVulnerability) =>
        presentVulnerabilities.includes(vulnerability),
    );

    if (prioritizedVulnerability) {
      const fromOtp = loginType === 'otp';

      setVulnerability(prioritizedVulnerability);
      setIsFromOtp(fromOtp);
    } else if (loginType === 'otp') {
      setVulnerability('no_vulnerabilities');
      setIsFromOtp(true);
    }
  }, [hasSearchParam, removeSearchParams, getQueryParam, vulnerability]);

  if (vulnerability === undefined) {
    return null;
  }

  const vulnerabilityTranslationBase = toCamelCase(vulnerability);
  const credentialType =
    vulnerability === 'is_password_leaked'
      ? 'exposed'
      : vulnerability === 'is_password_vulnerable'
      ? 'vulnerable'
      : 'noVulnerabilities';

  const closeModalAndCleanup = () => {
    replace(pathname);
    setVulnerability(undefined);
    setIsFromOtp(false);
  };

  const handleChangePassword = () => {
    if (isFromOtp)
      logEvent(OTP_MODAL_RECOMMENDATIONS_CLICK, {
        recommendation_type: 'change password',
      });
    const userFormRoute = ACCOUNT_ROUTES.userForm.replace(':id', currentUserId);
    openWindow(getNewAdminLink(userFormRoute), true);
    closeModalAndCleanup();
  };

  const handleEnable2FA = () => {
    if (isFromOtp)
      logEvent(OTP_MODAL_RECOMMENDATIONS_CLICK, {
        recommendation_type: 'activate 2fa',
      });
    openWindow(getNewAdminLink(ACCOUNT_ROUTES.authenticationFactor), true);
    closeModalAndCleanup();
  };

  const renderOriginalContent = () => (
    <>
      <Modal.Header>
        <Box padding="4" paddingBottom="none">
          <Text
            fontSize="highlight"
            fontWeight="bold"
            color="neutral-textHigh"
            lineHeight="base"
          >
            {vulnerablePasswordTranslations(
              `${vulnerabilityTranslationBase}.title`,
            )}
          </Text>
        </Box>
      </Modal.Header>
      <Modal.Body padding="none">
        <Box padding="4" paddingTop="none">
          <Text fontSize="base" color="neutral-textLow" lineHeight="base">
            {vulnerablePasswordTranslations(
              `${vulnerabilityTranslationBase}.text`,
            )}
          </Text>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box padding="4" paddingTop="none">
          <Stack justify="flex-start" flex="1">
            <Link
              as="a"
              href={guideUrl}
              target="_blank"
              rel="noopener noreferrer"
              appearance="primary"
              textDecoration="none"
            >
              {vulnerablePasswordTranslations('linkText')}
              <Icon color="currentColor" source={<ExternalLinkIcon />} />
            </Link>
          </Stack>
        </Box>
      </Modal.Footer>
    </>
  );

  const renderOtpContent = () => {
    if (credentialType === 'noVulnerabilities') {
      return (
        <>
          <Modal.Header>
            <Box padding="4" paddingBottom="none">
              <Text
                fontSize="highlight"
                fontWeight="bold"
                color="neutral-textHigh"
                lineHeight="base"
              >
                {securityModalTranslations('noVulnerabilities.title')}
              </Text>
            </Box>
          </Modal.Header>
          <Modal.Body padding="none">
            <Box padding="4" paddingTop="none">
              <Text fontSize="base" color="neutral-textLow" lineHeight="base">
                {securityModalTranslations('noVulnerabilities.description')}
              </Text>
            </Box>
            <InteractiveList bottomDivider={false}>
              <InteractiveList.ButtonItem
                title={securityModalTranslations('enable2FA.title')}
                iconButton={
                  {
                    onClick: handleEnable2FA,
                    source: <ExternalLinkIcon />,
                    color: 'primary-interactive',
                  } as any
                }
              />
            </InteractiveList>
            <Box paddingBottom="1" />
          </Modal.Body>
        </>
      );
    }

    return (
      <>
        <Modal.Header>
          <Box padding="4" paddingBottom="none">
            <Text
              fontSize="highlight"
              fontWeight="bold"
              color="neutral-textHigh"
              lineHeight="base"
            >
              {securityModalTranslations(`${credentialType}.title`)}
            </Text>
          </Box>
        </Modal.Header>
        <Modal.Body padding="none">
          <Box padding="4" paddingTop="none">
            <Text fontSize="base" color="neutral-textLow" lineHeight="base">
              {securityModalTranslations(`${credentialType}.description`)}
            </Text>
          </Box>
          <Box padding="4" paddingTop="none">
            <Text
              fontSize="base"
              fontWeight="bold"
              color="neutral-textHigh"
              lineHeight="base"
            >
              {securityModalTranslations('recommendations')}
            </Text>
          </Box>
          <InteractiveList bottomDivider={false}>
            <InteractiveList.ButtonItem
              title={securityModalTranslations('changePassword.title')}
              description={securityModalTranslations(
                `${credentialType}.changePasswordDescription`,
              )}
              iconButton={
                {
                  onClick: handleChangePassword,
                  source: <ExternalLinkIcon />,
                  color: 'primary-interactive',
                } as any
              }
            />
            <InteractiveList.ButtonItem
              title={securityModalTranslations('enable2FA.title')}
              description={securityModalTranslations('enable2FA.description')}
              iconButton={
                {
                  onClick: handleEnable2FA,
                  source: <ExternalLinkIcon />,
                  color: 'primary-interactive',
                } as any
              }
              topDivider
            />
          </InteractiveList>
          <Box paddingBottom="1" />
        </Modal.Body>
      </>
    );
  };

  return (
    <Modal open onDismiss={closeModalAndCleanup} padding="none">
      {isFromOtp ? renderOtpContent() : renderOriginalContent()}
    </Modal>
  );
}

export default VulnerablePasswordAlert;
